import { StorageInterface } from '../stores/childNavStores';
import { AuthorizedUserDto, FamilyAuthorizedUsers, AuthorizedUserSearchCriteria } from '../types/AuthorizedUserDto';
import { AccountGetInfoResponseDto } from '../types/api/service';

/**
 * AccountUserStorage - Manages local storage of account users and account info
 *
 * Storage Structure:
 * - Key: account_users_{familyId}       — FamilyAuthorizedUsers (account user list)
 * - Key: account_get_info_{accountId}   — AccountGetInfoResponseDto (full sync payload)
 */
export class AccountUserStorage {
  private storage: StorageInterface;
  private readonly userKeyPrefix = 'account_users_';
  private readonly infoKeyPrefix = 'account_get_info_';

  constructor(storage: StorageInterface) {
    this.storage = storage;
  }

  // ── Account User (formerly AuthorizedUser) methods ──────────────────────────

  /**
   * Save an account user for a family.
   * Automatically handles deduplication based on authorizedUserId.
   */
  async saveAccountUser(familyId: string, accountUser: AuthorizedUserDto): Promise<boolean> {
    try {
      console.log('💾 AccountUserStorage: Saving account user:', {
        familyId,
        accountUserId: accountUser.authorizedUserId,
        name: accountUser.name,
        userType: accountUser.userType,
        status: accountUser.status,
      });

      accountUser.familyId = familyId;

      const existing = await this.getFamilyUsers(familyId);
      const filtered = existing.users.filter(u => u.authorizedUserId !== accountUser.authorizedUserId);
      filtered.push(accountUser);

      const updated: FamilyAuthorizedUsers = {
        familyId,
        lastSyncDate: new Date().toISOString(),
        users: filtered,
      };

      await this.storage.setItem(this.userKeyPrefix + familyId, JSON.stringify(updated));
      console.log(`💾 AccountUserStorage: Save result: SUCCESS`);
      console.log(`   Total users for family ${familyId}: ${filtered.length}`);
      return true;
    } catch (error) {
      console.error('❌ AccountUserStorage: Error saving account user:', error);
      return false;
    }
  }

  /**
   * Get all account users for a family.
   */
  async getFamilyUsers(familyId: string): Promise<FamilyAuthorizedUsers> {
    try {
      const data = await this.storage.getItem(this.userKeyPrefix + familyId);
      if (data) {
        const parsed = JSON.parse(data) as FamilyAuthorizedUsers;
        console.log(`📋 AccountUserStorage: Loaded ${parsed.users.length} users for family ${familyId}`);
        return parsed;
      }
      console.log(`📋 AccountUserStorage: No users found for family ${familyId}, returning empty`);
      return { familyId, lastSyncDate: new Date().toISOString(), users: [] };
    } catch (error) {
      console.error('❌ AccountUserStorage: Error loading family users:', error);
      return { familyId, lastSyncDate: new Date().toISOString(), users: [] };
    }
  }

  /**
   * Get a specific account user by ID.
   */
  async getUserById(familyId: string, accountUserId: string): Promise<AuthorizedUserDto | null> {
    try {
      const familyUsers = await this.getFamilyUsers(familyId);
      const user = familyUsers.users.find(u => u.authorizedUserId === accountUserId);
      console.log(`🔍 AccountUserStorage: User lookup ${accountUserId}: ${user ? 'FOUND' : 'NOT FOUND'}`);
      return user ?? null;
    } catch (error) {
      console.error('❌ AccountUserStorage: Error getting user by ID:', error);
      return null;
    }
  }

  /**
   * Delete an account user.
   */
  async deleteAccountUser(familyId: string, accountUserId: string): Promise<boolean> {
    try {
      console.log(`🗑️ AccountUserStorage: Deleting user ${accountUserId} from family ${familyId}`);

      const existing = await this.getFamilyUsers(familyId);
      const filtered = existing.users.filter(u => u.authorizedUserId !== accountUserId);

      if (filtered.length === existing.users.length) {
        console.log(`⚠️ AccountUserStorage: User ${accountUserId} not found for deletion`);
        return false;
      }

      const updated: FamilyAuthorizedUsers = {
        familyId,
        lastSyncDate: new Date().toISOString(),
        users: filtered,
      };
      await this.storage.setItem(this.userKeyPrefix + familyId, JSON.stringify(updated));
      console.log(`🗑️ AccountUserStorage: Delete result: SUCCESS`);
      return true;
    } catch (error) {
      console.error('❌ AccountUserStorage: Error deleting account user:', error);
      return false;
    }
  }

  /**
   * Search account users based on criteria.
   */
  async searchUsers(criteria: AuthorizedUserSearchCriteria): Promise<AuthorizedUserDto[]> {
    try {
      const results: AuthorizedUserDto[] = [];

      if (criteria.familyId) {
        const familyUsers = await this.getFamilyUsers(criteria.familyId);
        results.push(...familyUsers.users);
      } else {
        const allKeys = await this.getAllFamilyKeys();
        for (const key of allKeys) {
          const familyId = key.replace(this.userKeyPrefix, '');
          const familyUsers = await this.getFamilyUsers(familyId);
          results.push(...familyUsers.users);
        }
      }

      let filtered = results;

      if (criteria.userType) {
        filtered = filtered.filter(u => u.userType === criteria.userType);
      }
      if (criteria.status) {
        filtered = filtered.filter(u => u.status === criteria.status);
      }
      if (criteria.namePattern) {
        const pattern = criteria.namePattern.toLowerCase();
        filtered = filtered.filter(u => u.name.toLowerCase().includes(pattern));
      }
      if (criteria.hasEmail !== undefined) {
        filtered = filtered.filter(u => (criteria.hasEmail ? !!u.email : !u.email));
      }
      if (criteria.hasPhone !== undefined) {
        filtered = filtered.filter((u: AuthorizedUserDto) => (criteria.hasPhone ? !!u.phone : !u.phone));
      }
      if (criteria.createdAfter) {
        const after = new Date(criteria.createdAfter);
        filtered = filtered.filter(u => new Date(u.createdDate) >= after);
      }
      if (criteria.createdBefore) {
        const before = new Date(criteria.createdBefore);
        filtered = filtered.filter(u => new Date(u.createdDate) <= before);
      }

      console.log(`🔍 AccountUserStorage: Search found ${filtered.length} users matching criteria`);
      return filtered;
    } catch (error) {
      console.error('❌ AccountUserStorage: Error searching users:', error);
      return [];
    }
  }

  /**
   * Clear all account users for a family.
   */
  async clearFamilyUsers(familyId: string): Promise<boolean> {
    try {
      console.log(`🧹 AccountUserStorage: Clearing all users for family ${familyId}`);
      await this.storage.removeItem(this.userKeyPrefix + familyId);
      console.log(`🧹 AccountUserStorage: Clear result: SUCCESS`);
      return true;
    } catch (error) {
      console.error('❌ AccountUserStorage: Error clearing family users:', error);
      return false;
    }
  }

  /**
   * Get storage statistics for account users.
   */
  async getStorageStats(): Promise<{ totalFamilies: number; totalUsers: number }> {
    try {
      const allKeys = await this.getAllFamilyKeys();
      let totalUsers = 0;
      for (const key of allKeys) {
        const familyId = key.replace(this.userKeyPrefix, '');
        const familyUsers = await this.getFamilyUsers(familyId);
        totalUsers += familyUsers.users.length;
      }
      return { totalFamilies: allKeys.length, totalUsers };
    } catch (error) {
      console.error('❌ AccountUserStorage: Error getting storage stats:', error);
      return { totalFamilies: 0, totalUsers: 0 };
    }
  }

  private async getAllFamilyKeys(): Promise<string[]> {
    try {
      console.warn('⚠️ AccountUserStorage: getAllKeys not available in StorageInterface - returning empty array');
      return [];
    } catch (error) {
      console.error('❌ AccountUserStorage: Error getting all keys:', error);
      return [];
    }
  }

  /**
   * Convert backend AuthorizedUserGetListItemDto to local AuthorizedUserDto.
   */
  static convertFromBackendItem(
    backendItem: { AuthorizedUserId: string; Name: string },
    familyId: string,
    additionalData?: Partial<AuthorizedUserDto>
  ): AuthorizedUserDto {
    const now = new Date().toISOString();
    return {
      authorizedUserId: backendItem.AuthorizedUserId,
      name: backendItem.Name,
      familyId,
      userType: additionalData?.userType ?? 'family_member',
      permissions: additionalData?.permissions ?? [],
      status: additionalData?.status ?? 'active',
      email: additionalData?.email,
      phone: additionalData?.phone,
      deviceId: additionalData?.deviceId,
      lastAccess: additionalData?.lastAccess,
      createdDate: additionalData?.createdDate ?? now,
      modifiedDate: additionalData?.modifiedDate ?? now,
      syncDate: now,
      ...additionalData,
    };
  }

  // ── AccountGetInfo methods ───────────────────────────────────────────────────

  /**
   * Save the full AccountGetInfoResponseDto returned from the account_get_info sync call.
   * Keyed by accountId so each account gets its own entry.
   */
  async saveAccountGetInfo(accountId: string, dto: AccountGetInfoResponseDto): Promise<boolean> {
    try {
      console.log('💾 AccountUserStorage: Saving AccountGetInfo for account:', {
        accountId,
        userCount: dto.accountusers?.length ?? 0,
        connectionCount: dto.connections?.length ?? 0,
        isPremium: dto.IsPremium,
        status: dto.Status,
      });
      const payload = { ...dto, _savedAt: new Date().toISOString() };
      await this.storage.setItem(this.infoKeyPrefix + accountId, JSON.stringify(payload));
      console.log(`💾 AccountUserStorage: AccountGetInfo saved for account ${accountId}`);
      return true;
    } catch (error) {
      console.error('❌ AccountUserStorage: Error saving AccountGetInfo:', error);
      return false;
    }
  }

  /**
   * Load the most recently saved AccountGetInfoResponseDto for an account.
   * Returns null if not found.
   */
  async getAccountGetInfo(accountId: string): Promise<AccountGetInfoResponseDto | null> {
    try {
      const data = await this.storage.getItem(this.infoKeyPrefix + accountId);
      if (!data) {
        console.log(`📋 AccountUserStorage: No AccountGetInfo found for account ${accountId}`);
        return null;
      }
      const parsed = JSON.parse(data) as AccountGetInfoResponseDto & { _savedAt?: string };
      console.log(`📋 AccountUserStorage: Loaded AccountGetInfo for account ${accountId}`, {
        userCount: parsed.accountusers?.length ?? 0,
        savedAt: (parsed as any)._savedAt,
      });
      return parsed;
    } catch (error) {
      console.error('❌ AccountUserStorage: Error loading AccountGetInfo:', error);
      return null;
    }
  }

  /**
   * Clear the stored AccountGetInfoResponseDto for an account.
   */
  async clearAccountGetInfo(accountId: string): Promise<boolean> {
    try {
      await this.storage.removeItem(this.infoKeyPrefix + accountId);
      console.log(`🧹 AccountUserStorage: Cleared AccountGetInfo for account ${accountId}`);
      return true;
    } catch (error) {
      console.error('❌ AccountUserStorage: Error clearing AccountGetInfo:', error);
      return false;
    }
  }
}
